import {
  IUsageDataMRPCRequestParams,
  IUsageDataMRPCResponse,
} from 'multirpc-sdk';

import { MultiService } from 'modules/api/MultiService';
import { createNotifyingQueryFn } from 'store/utils/createNotifyingQueryFn';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { makeWorkerGatewayAuthorization } from 'domains/jwtToken/utils/makeWorkerGatewayAuthorization';
import { web3Api } from 'store/queries';

import { REFETCH_STATS_INTERVAL, statsTimeframeToIntervalMap } from '../const';
import { getStatsTimeBorders } from '../utils/getStatsTimeBorders';
import { IFetchPublicStatsParams } from './fetchPublicStats';

export interface IFetchPrivateStatsParams extends IFetchPublicStatsParams {
  tokens?: IUsageDataMRPCRequestParams['api_key_values'];
  workerToken: string;
  unfiltered?: IUsageDataMRPCRequestParams['unfiltered'];
}

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be refelected there as well.
export const {
  endpoints: { fetchPrivateStats },
  useFetchPrivateStatsQuery,
  useLazyFetchPrivateStatsQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchPrivateStats: build.query<
      IUsageDataMRPCResponse,
      IFetchPrivateStatsParams
    >({
      keepUnusedDataFor: REFETCH_STATS_INTERVAL,
      queryFn: createNotifyingQueryFn(
        async ({
          blockchains,
          groupBy,
          limit,
          metrics,
          timeframe,
          timeframeShift,
          tokens,
          unfiltered,
          workerToken,
        }) => {
          makeWorkerGatewayAuthorization(workerToken);

          const { end, start } = getStatsTimeBorders({
            timeframe,
            timeframeShift,
          });
          const interval = statsTimeframeToIntervalMap[timeframe];
          const intervalInSeconds = `${interval}s`;

          const api = unfiltered
            ? MultiService.getService().getPublicGateway()
            : MultiService.getService().getWorkerGateway();

          const data = await api.getSharkTelemetry(
            {
              api_key_values: tokens,
              blockchains,
              end,
              include_top_ofs: metrics,
              interval: intervalInSeconds,
              start,
              top_limit: limit,
              group_by: groupBy,
            },
            { unfiltered },
          );

          return { data };
        },
      ),
    }),
  }),
  overrideExisting: true,
});

export const fetchPrivateStatsFallback: IUsageDataMRPCResponse = {
  all_time_total: 0,
  request_timelines: [],
  tops: [],
  total: 0,
};

export const {
  selectDataWithFallbackCachedByParams: selectPrivateStats,
  selectLoadingCachedByParams: selectPrivateStatsLoading,
  selectStateCachedByParams: selectPrivateStatsState,
} = createQuerySelectors({
  endpoint: fetchPrivateStats,
  fallback: fetchPrivateStatsFallback,
});
